import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useTranslation } from '../../hooks/useTranslation';
import { profileAPI, setAuthToken } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import PersonalInfoTab from '../../components/profile/PersonalInfoTab';
import CompanyInfoTab from '../../components/profile/CompanyInfoTab';
import BrandingTab from '../../components/profile/BrandingTab';
import SecurityTab from '../../components/profile/SecurityTab';
import PreferencesTab from '../../components/profile/PreferencesTab';
import { User, Building2, Palette, Shield, Settings, ArrowLeft } from 'lucide-react';

const ProfilePage = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('personal');

  useEffect(() => {
    if (token) {
      setAuthToken(token);
      loadProfile();
    }
  }, [token]);

  const loadProfile = async () => {
    try {
      const response = await profileAPI.getProfile();
      setProfile(response.data);
    } catch (error) {
      console.error('Failed to load profile', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { value: 'personal', label: t('profile.tabs.personal'), icon: User },
    { value: 'company', label: t('profile.tabs.company'), icon: Building2 },
    { value: 'branding', label: t('profile.tabs.branding'), icon: Palette },
    { value: 'security', label: t('profile.tabs.security'), icon: Shield },
    { value: 'preferences', label: t('profile.tabs.preferences'), icon: Settings },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="profile-page">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)}
          className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">{t('profile.title')}</h1>
          <p className="text-slate-600 mt-1">{t('profile.subtitle')}</p>
        </div>
      </div>

      {/* Tabs */}
      <Card className="p-6 border-slate-200">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-5 gap-2 bg-slate-100 p-1 rounded-lg">
            {tabs.map(({ value, label, icon: Icon }) => (
              <TabsTrigger
                key={value}
                value={value}
                className="flex items-center justify-center space-x-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-violet-600 data-[state=active]:text-white rounded-md transition-all"
                data-testid={`tab-${value}`}
              >
                <Icon className="h-4 w-4" />
                <span className="hidden md:inline text-sm">{label}</span>
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="personal" className="space-y-6">
            <PersonalInfoTab profile={profile} onUpdate={loadProfile} />
          </TabsContent>

          <TabsContent value="company" className="space-y-6">
            <CompanyInfoTab profile={profile} onUpdate={loadProfile} />
          </TabsContent>

          <TabsContent value="branding" className="space-y-6">
            <BrandingTab profile={profile} onUpdate={loadProfile} />
          </TabsContent>

          <TabsContent value="security" className="space-y-6">
            <SecurityTab />
          </TabsContent>

          <TabsContent value="preferences" className="space-y-6">
            <PreferencesTab profile={profile} onUpdate={loadProfile} />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default ProfilePage;
